import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Flame, Trophy, BookOpen, Upload, ArrowRight, Target, Headphones } from "lucide-react";
import { api, getUser } from "../lib/api";
import ActiveWordListPicker from "../components/ActiveWordListPicker";

const StatCard = ({ icon: Icon, label, value, color, shadow, testId }) => (
  <motion.div
    initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
    transition={{ type: "spring", bounce: 0.4 }}
    className="card-soft flex items-center gap-4"
    data-testid={testId}
  >
    <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center`} style={{ boxShadow: `0 6px 0 ${shadow}` }}>
      <Icon className="w-7 h-7 text-white" strokeWidth={3} />
    </div>
    <div>
      <div className="text-3xl font-display font-bold text-[#1E1B4B]">{value}</div>
      <div className="text-sm font-bold text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  </motion.div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lists, setLists] = useState([]);
  const [missedByList, setMissedByList] = useState({}); // {wordlistId: count}
  const [activePack, setActivePack] = useState(null); // resolved by school+grade
  const [activePickerKey, setActivePickerKey] = useState(0); // remount on change
  const user = getUser();

  const loadActive = async () => {
    try {
      const { data } = await api.get("/me/active-pack");
      const packs = data?.packs || [];
      const activeId = data?.active_id;
      const primary = packs.find((p) => p.id === activeId) || packs[0] || null;
      setActivePack(primary);
    } catch { /* tolerated */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get("/progress/stats"),
          api.get("/wordlists"),
        ]);
        setStats(s.data);
        setLists(l.data);
        await loadActive();
        // Fetch missed-word counts in parallel so we can badge the Take-a-test button
        const counts = await Promise.all((l.data || []).map(async (wl) => {
          try {
            const { data } = await api.get(`/wordlists/${wl.id}/missed-words`);
            return [wl.id, (data.words || []).length];
          } catch { return [wl.id, 0]; }
        }));
        setMissedByList(Object.fromEntries(counts));
      } catch (err) { console.warn("[Dashboard] load failed:", err); }
    })();
  }, []);

  // Use the school+grade-resolved pack first; fall back to the user's own
  // first list (existing behaviour). This way the existing per-user PDFs
  // still work for users who haven't set a school+grade yet.
  const primaryList = activePack || lists[0];
  const primaryMissed = primaryList ? (missedByList[primaryList.id] || 0) : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <motion.section
        initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="relative overflow-hidden rounded-[2rem] bg-white border-4 border-blue-100 p-8 sm:p-10 shadow-xl"
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-[#FFE4E6]" />
        <div className="absolute top-20 right-28 w-24 h-24 rounded-full bg-[#FEF3C7]" />
        <div className="relative">
          <div className="text-sm font-bold uppercase tracking-widest text-[#4D96FF]">Howdy{user?.name ? "," : ""} {user?.name}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1E1B4B] mt-2">
            Ready to <span className="text-[#FF6B6B]">spell</span> today?
          </h1>
          <p className="mt-3 text-lg text-slate-600 max-w-xl font-medium">
            Upload your spelling list, practice with a buzzy voice, and earn stars when you spell words right.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {primaryList ? (
              <>
                <Link to={`/practice/${primaryList.id}`} className="btn-clay-blue" data-testid="dashboard-practice-btn">
                  <ArrowRight className="w-5 h-5" strokeWidth={3} /> Practice now
                </Link>
                <Link to={`/listen/${primaryList.id}`} className="btn-clay-green" data-testid="dashboard-listen-btn">
                  <Headphones className="w-5 h-5" strokeWidth={3} /> Listen mode
                </Link>
                <Link to={`/test/${primaryList.id}`} className="btn-clay inline-flex items-center gap-2" data-testid="dashboard-test-btn">
                  <Target className="w-5 h-5" strokeWidth={3} /> Take a test
                  {primaryMissed > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[1.75rem] h-7 rounded-full bg-white text-[#9F1239] text-sm font-bold px-2 ml-1 shadow-[0_2px_0_#9F1239]"
                      data-testid="dashboard-missed-badge"
                    >
                      {primaryMissed}
                    </span>
                  )}
                </Link>
                {primaryMissed > 0 && (
                  <span className="text-sm font-bold text-[#9F1239] self-center ml-1" data-testid="dashboard-missed-hint">
                    {primaryMissed} to review
                  </span>
                )}
              </>
            ) : (
              <Link to="/wordlists" className="btn-clay" data-testid="dashboard-upload-btn-hero">
                <Upload className="w-5 h-5" strokeWidth={3} /> Upload your first list
              </Link>
            )}
          </div>
        </div>
      </motion.section>

      <section data-testid="dashboard-active-picker">
        <ActiveWordListPicker
          key={activePickerKey}
          compact
          onChanged={() => {
            // After switching active list, refetch /me/active-pack so the
            // hero "Practice / Listen / Test" buttons jump to the new list.
            setActivePickerKey((k) => k + 1);
            loadActive();
          }}
        />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        <StatCard icon={Star} label="Stars" value={stats?.stars ?? 0} color="bg-[#FBBF24]" shadow="#B45309" testId="stat-stars" />
        <StatCard icon={Flame} label="Day streak" value={stats?.current_streak ?? 0} color="bg-[#FF6B6B]" shadow="#9F1239" testId="stat-streak" />
        <StatCard icon={Trophy} label="Best streak" value={stats?.best_streak ?? 0} color="bg-[#4D96FF]" shadow="#1D4ED8" testId="stat-best" />
        <StatCard icon={Target} label="Mastered" value={stats?.mastered_count ?? 0} color="bg-[#10B981]" shadow="#047857" testId="stat-mastered" />
      </section>

      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1E1B4B]">Your word lists</h2>
          <Link to="/wordlists" className="text-[#4D96FF] font-bold hover:underline" data-testid="view-all-lists">See all →</Link>
        </div>
        {lists.length === 0 ? (
          <div className="card-soft text-center py-12" data-testid="no-lists-empty">
            <BookOpen className="w-12 h-12 mx-auto text-slate-300" strokeWidth={2} />
            <p className="mt-3 text-lg font-bold text-slate-600">No word lists yet</p>
            <p className="text-sm text-slate-500">Upload a PDF to get started.</p>
            <Link to="/wordlists" className="btn-clay mt-5 inline-flex">
              <Upload className="w-5 h-5" strokeWidth={3} /> Upload PDF
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {lists.slice(0, 6).map((l) => (
              <Link
                key={l.id} to={`/wordlists/${l.id}`}
                data-testid={`wordlist-card-${l.id}`}
                className="card-soft hover:-translate-y-1 transition-transform duration-200 block"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-[#4D96FF] flex items-center justify-center text-white shadow-[0_3px_0_#1D4ED8]">
                    <BookOpen className="w-5 h-5" strokeWidth={3} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {missedByList[l.id] > 0 && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-[#9F1239] px-2 py-0.5 text-xs font-bold border border-rose-200"
                        data-testid={`missed-chip-${l.id}`}
                        title={`${missedByList[l.id]} missed words to review`}
                      >
                        <Target className="w-3 h-3" strokeWidth={3} />
                        {missedByList[l.id]}
                      </span>
                    )}
                    <span className="chip">{l.word_count} words</span>
                  </div>
                </div>
                <div className="mt-3 font-display text-xl font-bold text-[#1E1B4B] line-clamp-2">{l.name}</div>
                <div className="text-sm text-slate-500 mt-1">Tap to practice or test</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {lists.length > 0 && (
        <section className="pt-2" data-testid="upload-footer">
          <div className="card-soft flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div>
              <div className="font-display text-xl font-bold text-[#1E1B4B]">Got a new spelling list?</div>
              <div className="text-sm text-slate-600">Upload a PDF and we'll turn it into a fun set of words.</div>
            </div>
            <Link to="/wordlists" className="btn-clay" data-testid="dashboard-upload-btn">
              <Upload className="w-5 h-5" strokeWidth={3} /> Upload a word list
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
